/**
 * Theme — the user's Appearance choice: 'dark' (default), 'light' or 'system'
 * (follow the device).
 *
 * The choice lives on the account (users.preferences.theme) so it follows the
 * user to another device and to Lead Concierge, and is cached in localStorage
 * so public/index.html can apply it before the first paint. The colours
 * themselves come from tailwind.config.js / theme.palette.js: setting
 * <html data-theme="light"> is all it takes to switch.
 */
import axios from 'axios';
import API_BASE_URL from './apiConfig';

export const THEMES = ['dark', 'light', 'system'];
const KEY = 'northai_theme';
const BAR = { dark: '#111827', light: '#f9fafb' };

const media = () => (typeof window !== 'undefined' && window.matchMedia)
  ? window.matchMedia('(prefers-color-scheme: light)') : null;

export function storedTheme() {
  try {
    const t = localStorage.getItem(KEY);
    return THEMES.includes(t) ? t : 'dark';
  } catch {
    return 'dark';
  }
}

/** 'light' | 'dark' — what a preference shows right now. */
export function resolveTheme(pref) {
  if (pref === 'system') return media()?.matches ? 'light' : 'dark';
  return pref === 'light' ? 'light' : 'dark';
}

let current = storedTheme();
const listeners = new Set();

function paint() {
  const shown = resolveTheme(current);
  const root = document.documentElement;
  if (shown === 'light') root.setAttribute('data-theme', 'light');
  else root.removeAttribute('data-theme');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BAR[shown]);
  listeners.forEach((fn) => fn(shown, current));
}

// A 'system' choice follows the device as it changes (e.g. at sunset).
media()?.addEventListener?.('change', () => { if (current === 'system') paint(); });

/** Apply a preference locally (no server call). */
export function applyTheme(pref) {
  current = THEMES.includes(pref) ? pref : 'dark';
  try { localStorage.setItem(KEY, current); } catch { /* private mode */ }
  paint();
}

/** Apply and save to the account. Resolves when saved; local change is immediate. */
export async function saveTheme(pref) {
  applyTheme(pref);
  await axios.patch(`${API_BASE_URL}/api/users/me/preferences`, { theme: current }, { withCredentials: true });
}

/** Adopt the account's saved choice after login (another device may have changed it). */
export function adoptAccountTheme(user) {
  const t = user?.preferences?.theme;
  if (THEMES.includes(t) && t !== current) applyTheme(t);
}

export function currentTheme() {
  return current;
}

/** Subscribe to changes: fn(shownTheme, preference). Returns unsubscribe. */
export function onThemeChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
